import { MAX_PALLET_QUANTITY } from '@stopal/shared/pallet-spec'

/**
 * What an estimate carries into an order surface, as URL search params.
 *
 * **Two params, and only two.** The pallet count, and the protection kit the
 * advisor named. The packing tier is deliberately absent: it is a function of
 * the count, and `preselectedKitIds` re-derives it there from the same
 * `recommendedKitId` the checkout badge uses. Carrying it as well would be a
 * second source for one question — the exact shape that lets the rundown and
 * the sheet disagree once the customer edits the count on arrival.
 *
 * One declaration, read by both destinations (`/checkout` for a guest, `/app`
 * for a signed-in customer), because a param the estimator emits and a
 * destination validates differently is a hand-off that silently drops.
 */
export interface EstimateHandoffSearch {
  pallets?: number
  kit?: string
}

/**
 * Longest kit id we will carry. The id is authoritatively checked against the
 * live catalog by `preselectedKitIds` — anything that is not an active
 * protection kit is dropped there — so this only keeps an absurd string out of
 * the URL and out of React state.
 */
const MAX_KIT_ID_CHARS = 64

/**
 * Read the hand-off out of a URL, treating every part of it as hostile.
 *
 * A search param is attacker-writable, and `addOnProductIds` flows into the
 * quote and the checkout line items untouched, so "it came from our own
 * rundown" is not a property this function may assume. The count is clamped to
 * the same shared ceiling the stepper enforces rather than trusted: without that, a
 * crafted link puts an arbitrary quantity into an order that a human never
 * typed.
 */
export function validateEstimateHandoffSearch(
  search: Record<string, unknown>,
): EstimateHandoffSearch {
  // Router search values arrive as strings from a real URL and as numbers from
  // a programmatic `navigate({ search })` — accept both, reject everything else.
  const raw = search.pallets
  const pallets =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
        ? Number(raw)
        : Number.NaN

  const kit = search.kit
  const isKitAcceptable =
    typeof kit === 'string' && kit.length > 0 && kit.length <= MAX_KIT_ID_CHARS

  // Both keys are always present, `undefined` when refused. TanStack Router
  // builds a route's search as `{ ...parentSearch, ...validated }`, and the
  // parent's is the RAW URL — so a key a validator merely leaves out is filled
  // straight back in with the unvalidated string.
  return {
    pallets:
      Number.isFinite(pallets) && pallets >= 1
        ? Math.min(Math.floor(pallets), MAX_PALLET_QUANTITY)
        : undefined,
    kit: isKitAcceptable ? kit : undefined,
  }
}
